import { type JSONData } from "../../base_classes/dto.d";
import { AirlinePricingDto } from "./dto.gen";
import { type IAirlinePricing } from "./entity.d";
import AirlinePricingParsers from "./parsers";
import AirlinePricingValidators from "./custom_validators";
import { Exclude } from "class-transformer";

export class AirlinePricing
  extends AirlinePricingDto
  implements IAirlinePricing
{
  constructor(
    pathVars?:
      | string
      | string[]
      | {
          id?: string;
        },
    data?: JSONData,
  ) {
    super(data, AirlinePricingParsers, AirlinePricingValidators);

    if (data !== undefined) {
      // Set data
      this.set(data);
    }

    this.buildPathObject(pathVars);
  }
  /// ///////////////////////////////////////
  /// //////// PRIVATE VARIABLES ////////////
  /// ///////////////////////////////////////

  /// ///////////////////////////////////////
  /// ///////// PUBLIC VARIABLES ////////////
  /// ///////////////////////////////////////

  /// ///////////////////////////////////////
  /// ////////////// METHODS ////////////////
  /// ///////////////////////////////////////

  public async get(
    pathVars?:
      | string
      | string[]
      | {
          id?: string;
        }
      | undefined,
  ): Promise<AirlinePricing> {
    return (await super.get(pathVars)) as AirlinePricing;
  }

  /**
   * Find the unit costs for a given number of colors and units
   * @returns {number} The unit costs
   */
  @Exclude()
  findUnitCosts(colors: number, units: number): number {
    let returnValue = 0;
    let lowerUnitBound = 0;

    // Get lower bounds
    const { lowestColorKey, lowestUnitKey } = this.getLowerBounds();

    // Failsafe
    colors = this.validateParam(colors, lowestColorKey);
    units = this.validateParam(units, lowestUnitKey);

    // Find unit costs
    for (const { attributes, value } of this.values) {
      if (attributes.colors === colors) {
        if (attributes.units <= units && attributes.units > lowerUnitBound) {
          returnValue = value.floatValue;
          lowerUnitBound = attributes.units;
        }
      }
    }

    return returnValue;
  }

  /**
   * Get the lower bounds of the current pricing table
   * @returns {{ lowestColorKey: number; lowestUnitKey: number }} The lowest color key and unit key
   */
  @Exclude()
  getLowerBounds(): { lowestColorKey: number; lowestUnitKey: number } {
    // Find lowest unit and color key
    const lowestColorKey = Math.min(
      ...this.values.map((p: any) => parseInt(p.attributes.colors)),
    );
    const lowestUnitKey = Math.min(
      ...this.values.map((p: any) => parseInt(p.attributes.units)),
    );

    return {
      lowestColorKey,
      lowestUnitKey,
    };
  }

  /**
   * Validate a param to check if it is a number and at least equal to the lower bound
   * @returns {number} The validated parameter
   */
  @Exclude()
  validateParam(
    param: string | number | undefined,
    lowerBound: number,
  ): number {
    // Failsafe
    if (!param) {
      param = 0;
    }

    // Parse integer
    param = parseInt(param?.toString() || "0");

    // Check lower bound
    if (param < lowerBound) {
      param = lowerBound;
    }

    return param;
  }

  /// ///////////////////////////////////////
  /// /////////// STATIC METHODS ////////////
  /// ///////////////////////////////////////

  static override FromJSON(data?: JSONData): AirlinePricing {
    return new AirlinePricing(undefined, data);
  }

  static override FromJSONArray(data: JSONData[]): AirlinePricing[] {
    return data.map((item) => AirlinePricing.FromJSON(item));
  }

  static FromDto(dto: AirlinePricingDto): AirlinePricing {
    return new AirlinePricing(undefined, dto.toJSON());
  }
}
